import { useState, useEffect } from 'react';
import { Filter } from 'lucide-react';
import { format } from 'date-fns';

interface DashboardFiltersProps {
  onFilterChange: (filters: {
    dates: { from: string | null; to: string | null };
    tags: string[];
    category: string | null;
  }) => void;
  isLoading?: boolean;
}

export function DashboardFilters({ onFilterChange, isLoading = false }: DashboardFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleApplyFilters = () => {
    onFilterChange({
      dates: {
        from: dateFrom || null,
        to: dateTo || null,
      },
      tags: selectedTags,
      category: selectedCategory || null,
    });
    setIsOpen(false);
  };

  // Validate that the to date is not before the from date
  useEffect(() => {
    if (dateFrom && dateTo && new Date(dateTo) < new Date(dateFrom)) {
      setDateTo(dateFrom);
    }
  }, [dateFrom]);

  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedCategory('');
    setSelectedTags([]);
    onFilterChange({
      dates: { from: null, to: null },
      tags: [],
      category: null,
    });
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
          isLoading 
            ? 'bg-gray-100 cursor-not-allowed' 
            : 'hover:bg-gray-50 cursor-pointer'
        }`}
      >
        <Filter className={`w-5 h-5 ${isLoading ? 'text-gray-400' : 'text-gray-600'}`} />
        <span className={isLoading ? 'text-gray-400' : 'text-gray-600'}>Filtros</span>
        {(dateFrom || dateTo || selectedCategory || selectedTags.length > 0) && (
          <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
            Activos
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border p-4 z-10">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                max={dateTo || undefined}
                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todas</option>
                <option value="B2B">SaaS B2B</option>
                <option value="B2C">SaaS B2C</option>
                <option value="Finance">Finanzas</option>
                <option value="Education">Educación</option>
                <option value="Health">Salud</option>
                <option value="E-commerce">E-commerce</option>
                <option value="Marketplace">Marketplace</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etiquetas
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {[
                  'form',
                  'prototype',
                  'validation',
                  'test',
                  'landing',
                  'beta',
                  'production',
                  'development',
                  'B2B',
                  'SaaS'
                ].map((tag) => (
                  <label key={tag} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTags([...selectedTags, tag]);
                        } else {
                          setSelectedTags(selectedTags.filter((t) => t !== tag));
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">{tag}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Limpiar
              </button>
              <button
                onClick={handleApplyFilters}
                disabled={isLoading}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  isLoading
                    ? 'bg-primary-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {isLoading ? 'Aplicando...' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 